import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { ErrorState, Loading, PageHeader, Panel } from '../ui/Page';
import { LOCALE_NAMES } from './locales';
import { StoredImage } from '../ui/StoredImage';
import {
  asClock,
  fromClock,
  getBranding,
  getTenantSettings,
  listTenantOptions,
  removeLogo,
  updateBranding,
  updateTenantSettings,
  uploadLogo,
  WEEKDAYS,
  type TenantSettings,
} from './tenant-api';

/**
 * What the company decided about itself: where it is, what it speaks, and when it works.
 *
 * None of this is decoration. The timezone is what every scheduler reasons in, the working week
 * is what an SLA clock counts and what "wait until business hours" waits for, the language is
 * what the server writes e-mail in, and the currency is every amount on every screen. They were
 * columns nothing could change: a tenant outside São Paulo had all four wrong and no way to say
 * so.
 */
export function CompanyPage(): React.JSX.Element {
  const client = useQueryClient();

  const settings = useQuery({ queryKey: ['tenant'], queryFn: getTenantSettings });
  const options = useQuery({
    queryKey: ['tenant-options'],
    queryFn: listTenantOptions,
    staleTime: 60 * 60 * 1000,
  });

  const [draft, setDraft] = useState<TenantSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settings.data) setDraft(settings.data);
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () =>
      updateTenantSettings({
        name: draft!.name,
        timezone: draft!.timezone,
        locale: draft!.locale,
        currency: draft!.currency,
        businessHours: draft!.businessHours,
        inboxReopen: draft!.inboxReopen,
        inboxBotClosing: draft!.inboxBotClosing,
        inboxSignature: draft!.inboxSignature,
        salesTargetCents: draft!.salesTargetCents,
      }),
    onSuccess: (next) => {
      setDraft(next);
      setError(null);
      setSaved(true);
      /*
       * Everything on screen was formatted with the old values, so the whole cache goes.
       * Refetching one query would leave dates elsewhere written in a timezone that no longer
       * applies, which is the sort of inconsistency nobody reports and everybody distrusts.
       */
      void client.invalidateQueries();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  if (settings.isLoading || options.isLoading) return <Loading />;
  if (settings.error) {
    return <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />;
  }
  if (!draft) return <Loading />;

  const edit = (change: Partial<TenantSettings>): void => {
    setDraft({ ...draft, ...change });
    setSaved(false);
  };

  const hours = draft.businessHours;
  const dirty = JSON.stringify(draft) !== JSON.stringify(settings.data);

  return (
    <div className="view">
      <PageHeader
        title={t('company.title')}
        description={t('company.description')}
        actions={
          <div className="cell-inline">
            {saved && <span className="muted small">{t('company.saved')}</span>}
            <button
              type="button"
              className="button button--primary button--compact"
              disabled={!dirty || save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? t('company.saving') : t('company.save')}
            </button>
          </div>
        }
      />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <div className="home-grid">
        <Panel title={t('company.identity')}>
          <label className="stack__field">
            <span>{t('crm.field.name')}</span>
            <input
              value={draft.name}
              maxLength={200}
              onChange={(event) => edit({ name: event.target.value })}
            />
          </label>

          <dl className="facts">
            <dt>{t('pages.slug')}</dt>
            <dd>
              <code>{draft.slug}</code>
            </dd>
          </dl>
          <p className="muted small">{t('company.slugFixed')}</p>
        </Panel>

        <Panel title={t('company.regional')}>
          <label className="stack__field">
            <span>{t('company.timezone')}</span>
            <select
              className="select"
              value={draft.timezone}
              onChange={(event) => edit({ timezone: event.target.value })}
            >
              {(options.data?.timezones ?? [draft.timezone]).map((zone) => (
                <option key={zone} value={zone}>
                  {zone}
                </option>
              ))}
            </select>
            <span className="muted small">{t('company.timezoneHint')}</span>
          </label>

          <label className="stack__field">
            <span>{t('company.locale')}</span>
            <select
              className="select"
              value={draft.locale}
              onChange={(event) => edit({ locale: event.target.value })}
            >
              {(options.data?.locales ?? [draft.locale]).map((locale) => (
                <option key={locale} value={locale}>
                  {LOCALE_NAMES[locale] ?? locale}
                </option>
              ))}
            </select>
            <span className="muted small">{t('company.localeHint')}</span>
          </label>

          <label className="stack__field">
            <span>{t('company.currency')}</span>
            <select
              className="select"
              value={draft.currency}
              onChange={(event) => edit({ currency: event.target.value })}
            >
              {(options.data?.currencies ?? [draft.currency]).map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
          </label>
        </Panel>
      </div>

      <Panel title={t('company.hours')}>
        <p className="muted small">{t('company.hoursHint')}</p>

        <div className="cell-inline">
          <label className="stack__field">
            <span>{t('company.opensAt')}</span>
            <input
              type="time"
              value={asClock(hours.startMinute)}
              onChange={(event) => {
                const minute = fromClock(event.target.value);

                if (minute !== null) edit({ businessHours: { ...hours, startMinute: minute } });
              }}
            />
          </label>

          <label className="stack__field">
            <span>{t('company.closesAt')}</span>
            <input
              type="time"
              value={asClock(hours.endMinute)}
              onChange={(event) => {
                const minute = fromClock(event.target.value);

                if (minute !== null) edit({ businessHours: { ...hours, endMinute: minute } });
              }}
            />
          </label>
        </div>

        <div className="stack__field">
          <span>{t('company.days')}</span>
          <div className="cell-inline">
            {WEEKDAYS.map((day) => (
              <button
                key={day}
                type="button"
                className={`chip${hours.days.includes(day) ? ' chip--active' : ''}`}
                aria-pressed={hours.days.includes(day)}
                onClick={() =>
                  edit({
                    businessHours: {
                      ...hours,
                      days: hours.days.includes(day)
                        ? hours.days.filter((other) => other !== day)
                        : [...hours.days, day].sort((a, b) => a - b),
                    },
                  })
                }
              >
                {t(`company.weekday.${day}`)}
              </button>
            ))}
          </div>

          {/* The server refuses it too; saying so here spares somebody the round trip. */}
          {hours.days.length === 0 && (
            <span className="lead-idle small">{t('company.needADay')}</span>
          )}
          {hours.endMinute <= hours.startMinute && (
            <span className="lead-idle small">{t('company.badRange')}</span>
          )}
        </div>
      </Panel>

      {/*
        The rule that used to be two contradictory constants.

        An inbound message reopened a resolved conversation within a fixed day; an agent writing
        first appended to it and left it resolved, so the message went out and the thread stayed
        invisible under the "Aberta" filter. Which of the two behaviours is right is a decision
        about how the desk works, so the desk makes it.
      */}
      <Panel title={t('company.reopenTitle')} description={t('company.reopenBody')}>
        <div className="field">
          <label className="field__label" htmlFor="reopen-policy">
            {t('company.reopenPolicy')}
          </label>
          <select
            id="reopen-policy"
            className="input"
            value={draft.inboxReopen.policy}
            onChange={(event) =>
              edit({
                inboxReopen: {
                  ...draft.inboxReopen,
                  policy: event.target.value === 'NEW' ? 'NEW' : 'REOPEN',
                },
              })
            }
          >
            <option value="REOPEN">{t('company.reopenContinue')}</option>
            <option value="NEW">{t('company.reopenAlwaysNew')}</option>
          </select>
        </div>

        {draft.inboxReopen.policy === 'REOPEN' && (
          <div className="field">
            <label className="field__label" htmlFor="reopen-window">
              {t('company.reopenWindow')}
            </label>
            <select
              id="reopen-window"
              className="input"
              value={String(draft.inboxReopen.windowMinutes)}
              onChange={(event) =>
                edit({
                  inboxReopen: {
                    ...draft.inboxReopen,
                    windowMinutes: Number(event.target.value),
                  },
                })
              }
            >
              <option value="60">{t('company.reopenWindow1h')}</option>
              <option value="480">{t('company.reopenWindow8h')}</option>
              <option value="1440">{t('company.reopenWindow24h')}</option>
              <option value="4320">{t('company.reopenWindow3d')}</option>
              <option value="10080">{t('company.reopenWindow7d')}</option>
              <option value="43200">{t('company.reopenWindow30d')}</option>
            </select>
            <span className="lead-idle small">{t('company.reopenWindowHelp')}</span>
          </div>
        )}
      </Panel>

      {/*
        * When a conversation the bot is handling ends by itself (Phase 109).
        *
        * Off by default, and deliberately: most conversations end because the customer got their
        * answer and stopped writing, but a silence is not by itself a finished conversation, and a
        * desk that has never thought about it should not have its threads closed by a number this
        * product chose. Only bot-handled threads — a timer must not decide that somebody's own work
        * is finished.
        */}
      {/*
        * Whether a reply carries the name of whoever wrote it (Phase 113).
        *
        * A desk decision, not a product one: a small team wants "Lucas" on every reply, a support
        * queue wants nobody's name at all, and a regulated desk may be required to identify the
        * operator. The person answering can still turn it off for a single message.
        */}
      <Panel title={t('company.signatureTitle')} description={t('company.signatureBody')}>
        <label className="field field--inline">
          <input
            type="checkbox"
            checked={draft.inboxSignature.enabled}
            onChange={(event) =>
              edit({ inboxSignature: { ...draft.inboxSignature, enabled: event.target.checked } })
            }
          />
          <span>{t('company.signatureEnabled')}</span>
        </label>

        {draft.inboxSignature.enabled && (
          <div className="field">
            <label className="field__label" htmlFor="signature-format">
              {t('company.signatureFormat')}
            </label>
            <input
              id="signature-format"
              className="input"
              maxLength={200}
              value={draft.inboxSignature.format}
              onChange={(event) =>
                edit({ inboxSignature: { ...draft.inboxSignature, format: event.target.value } })
              }
            />
            <span className="lead-idle small">{t('company.signatureFormatHelp')}</span>
          </div>
        )}
      </Panel>

      <Panel title={t('company.botClosingTitle')} description={t('company.botClosingBody')}>
        <div className="field">
          <label className="field__label" htmlFor="closing-minutes">
            {t('company.botClosingWhen')}
          </label>
          <select
            id="closing-minutes"
            className="input"
            value={draft.inboxBotClosing.minutes === null ? '' : String(draft.inboxBotClosing.minutes)}
            onChange={(event) =>
              edit({
                inboxBotClosing: {
                  ...draft.inboxBotClosing,
                  minutes: event.target.value ? Number(event.target.value) : null,
                },
              })
            }
          >
            <option value="">{t('company.botClosingNever')}</option>
            <option value="30">{t('company.botClosing30m')}</option>
            <option value="60">{t('company.botClosing1h')}</option>
            <option value="240">{t('company.botClosing4h')}</option>
            <option value="1440">{t('company.botClosing24h')}</option>
          </select>
        </div>

        {draft.inboxBotClosing.minutes !== null && (
          <div className="field">
            <label className="field__label" htmlFor="closing-message">
              {t('company.botClosingMessage')}
            </label>
            <textarea
              id="closing-message"
              className="input"
              rows={2}
              maxLength={1000}
              value={draft.inboxBotClosing.message ?? ''}
              onChange={(event) =>
                edit({
                  inboxBotClosing: { ...draft.inboxBotClosing, message: event.target.value },
                })
              }
            />
            <span className="lead-idle small">{t('company.botClosingMessageHelp')}</span>
          </div>
        )}
      </Panel>

      <Panel title={t('company.salesTarget')} description={t('company.salesTargetHint')}>
        <div className="field">
          <input
            className="input"
            type="number"
            min={0}
            step="100"
            value={draft.salesTargetCents === null ? '' : draft.salesTargetCents / 100}
            onChange={(event) =>
              edit({
                // Empty is a decision — "we do not work with a target" — and null is how the API
                // spells it; zero would be a target of nothing.
                salesTargetCents:
                  event.target.value.trim() === ''
                    ? null
                    : Math.round(Number(event.target.value) * 100),
              })
            }
          />
        </div>
      </Panel>

      <BrandingPanel />
    </div>
  );
}

/**
 * The company's own face (Phase 118).
 *
 * Its own panel and its own save, because it is the one thing on this screen that changes while you
 * look at it: the colour repaints the product the moment it is stored, which is the only honest way
 * to choose one.
 */
function BrandingPanel(): React.JSX.Element {
  const client = useQueryClient();
  const branding = useQuery({ queryKey: ['tenant-branding'], queryFn: getBranding });

  const [color, setColor] = useState('');
  const [css, setCss] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!branding.data || touched) return;

    setColor(branding.data.color ?? '');
    setCss(branding.data.customCss ?? '');
  }, [branding.data, touched]);

  // Everything here changes the shell, so both queries have to go.
  const settled = (): void => {
    setError(null);
    void client.invalidateQueries({ queryKey: ['tenant-branding'] });
    void client.invalidateQueries({ queryKey: ['tenant'] });
  };

  const save = useMutation({
    mutationFn: () => updateBranding({ color: color.trim() || null, customCss: css.trim() || null }),
    onSuccess: settled,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const logo = useMutation({
    mutationFn: async (file: File) => {
      const buffer = await file.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      let binary = '';

      for (const byte of bytes) binary += String.fromCharCode(byte);

      return uploadLogo({
        filename: file.name,
        mimeType: file.type,
        content: btoa(binary),
      });
    },
    onSuccess: settled,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const drop = useMutation({
    mutationFn: removeLogo,
    onSuccess: settled,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <Panel title={t('company.brandingTitle')} description={t('company.brandingBody')}>
      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <div className="field">
        <span className="field__label">{t('company.brandLogo')}</span>

        <div className="cell-inline">
          {branding.data?.logoFileId && (
            <StoredImage fileId={branding.data.logoFileId} alt="" className="brand-preview" />
          )}

          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            className="input"
            disabled={logo.isPending}
            onChange={(event) => {
              const file = event.target.files?.[0];

              if (file) logo.mutate(file);
            }}
          />

          {branding.data?.logoFileId && (
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => drop.mutate()}
              disabled={drop.isPending}
            >
              {t('company.brandLogoRemove')}
            </button>
          )}
        </div>

        <span className="lead-idle small">{t('company.brandLogoHelp')}</span>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="brand-color">
          {t('company.brandColor')}
        </label>

        <div className="cell-inline">
          <input
            id="brand-color"
            type="color"
            className="input input--color"
            value={color || '#2d5bff'}
            onChange={(event) => {
              setTouched(true);
              setColor(event.target.value);
            }}
          />
          <input
            className="input"
            value={color}
            placeholder="#2d5bff"
            maxLength={7}
            onChange={(event) => {
              setTouched(true);
              setColor(event.target.value);
            }}
          />
          {color !== '' && (
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => {
                setTouched(true);
                setColor('');
              }}
            >
              {t('company.brandColorClear')}
            </button>
          )}
        </div>

        <span className="lead-idle small">{t('company.brandColorHelp')}</span>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="brand-css">
          {t('company.brandCss')}
        </label>
        <textarea
          id="brand-css"
          className="input input--code"
          rows={6}
          value={css}
          onChange={(event) => {
            setTouched(true);
            setCss(event.target.value);
          }}
        />
        <span className="lead-idle small">{t('company.brandCssHelp')}</span>
      </div>

      <button
        type="button"
        className="button button--primary"
        onClick={() => save.mutate()}
        disabled={save.isPending}
      >
        {t('common.save')}
      </button>
    </Panel>
  );
}
